import Alert from "@mui/material/Alert";
import { Files as FilesIcon } from "lucide-react";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { FileMetadataAndTopics } from "@/shared/components/FileMetadataAndTopics";
import { optionConditionsOf } from "@/shared/components/filters/filterToCondition";
import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  activeFiltersToQuerySubmission,
  renderPrincipal,
  toOptionsResolver,
  useQuickSearchHotkey,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useResourceTableContext,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type FilterDefinition,
  type QuickSearchProperty,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  FILE_FILTER_FIELDS,
  FILE_FILTER_FIELDS_BY_STATUS,
  hydrateFilterDefinitions,
  INGESTION_STATUS_LABELS,
} from "@/shared/components/ResourceView/Filters/catalog";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { AssociationType } from "@/shared/domain/association";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { FileRecord, ingestionLabel } from "@/shared/domain/files";
import {
  filesScopeToCondition,
  type FilesQueryScope,
} from "@/shared/domain/files/FilesQueryScope";
import {
  fileQueryKeys,
  useFileMetadataKeys,
  useOrgFileTagSearch,
} from "@/shared/domain/files/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useFileTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  EmptyCell,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation/Goto";
import { formatByteSize } from "@/shared/text";

import styles from "./FilesList.module.css";
import { IngestionLabelBadge } from "./IngestionLabelBadge";
import { LatestIngestionRunsProvider } from "./LatestIngestionRunsProvider";

const getFileId: EntityIdGetter<FileRecord> = (f) => f.file_id;

const INGESTION_COLUMN_ID = "ingestion_status";
const NO_FILES: FileRecord[] = [];

/** What the Ingestion filter's options stand for; see `INGESTION_LABEL_OPTIONS`. */
const FILE_OPTION_CONDITIONS = optionConditionsOf(FILE_FILTER_FIELDS);

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Files };
// Declared beside the config that makes the filter bar RoboQL-capable, so the
// two cannot drift: a list that hands `ROBOQL_CONFIG` to its filters must also
// let a RoboQL View be applied.
const VIEW_LOAD_OPTIONS = { roboql: true } as const;

// Quick find routes a typed value to one field: an `fl_`-shaped value to the
// id, everything else to the name. Categorical filters (tags) live in the
// Filter menu, not here.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  {
    field: "file_id",
    name: "ID",
    // A file id is `fl_` plus non-space characters — anchored so a phrase
    // merely starting that way falls through to the name search.
    pattern: /^fl_\S+$/,
  },
  { field: "name", name: "Name", fallback: true },
];

const columnHelper = createColumnHelper<FileRecord>();

const STATIC_FILE_COLUMNS: ColDef<FileRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const fileId = p.data?.file_id;
      const name = p.value ?? "";
      if (!name) {
        return <span className={cellStyles.placeholder}>Untitled</span>;
      }
      if (!fileId) {
        return <span className={cellStyles.primaryContent}>{name}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createFileRoute(fileId)}
          className={cellStyles.primaryContent}
        >
          {name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 160,
    sortable: true,
    variant: "relative",
  }),
  {
    colId: "source",
    field: "origination",
    headerName: "Source",
    minWidth: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (!value) {
        return <EmptyCell />;
      }
      if (value.startsWith("iv_")) {
        return (
          <RobotoLinkHoverUnderline
            to={Goto.createInvocationRoute(value)}
            className={cellStyles.idCell}
          >
            {value}
          </RobotoLinkHoverUnderline>
        );
      }
      return value;
    },
  },
  columnHelper.accessor("association_id", {
    headerName: "Association ID",
    minWidth: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (!value) {
        return <EmptyCell />;
      }
      // A topic has no page listing its files, and
      // `Goto.createAssociationRoute` would read a topic ID as a dataset ID.
      if (p.data?.association_type === AssociationType.Topic) {
        return <span className={cellStyles.idCell}>{value}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createAssociationRoute(value)}
          className={cellStyles.idCell}
        >
          {value}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<FileRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    minWidth: 200,
    sortable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createFileRoute(id)}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    minWidth: 160,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    minWidth: 150,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("size", {
    headerName: "Size",
    minWidth: 100,
    sortable: false,
    valueFormatter: (p) =>
      p.value === null || p.value === undefined ? "" : formatByteSize(p.value),
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org ID",
    minWidth: 150,
    sortable: false,
    cellClass: cellStyles.idCell,
  }),
  columnHelper.accessor("uri", {
    headerName: "URI",
    minWidth: 150,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    minWidth: 110,
    sortable: false,
  }),
  columnHelper.accessor("upload_id", {
    headerName: "Upload ID",
    minWidth: 180,
    sortable: false,
    cellClass: cellStyles.idCell,
  }),
  columnHelper.accessor("device_id", {
    headerName: "Device ID",
    minWidth: 150,
    sortable: false,
    cellClass: cellStyles.idCell,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderDashOrValue,
  }),
  {
    colId: INGESTION_COLUMN_ID,
    headerName: "Ingestion",
    minWidth: 170,
    sortable: false,
    // One label from `ingestion_status` and `ingestable` together, so a file
    // no rule matches does not read as waiting on ingestion. The cells also
    // read each file's latest run (`LatestIngestionRunsProvider`), so a
    // failed or running ingestion shows as such; the Ingestion filter
    // (`INGESTION_LABEL_OPTIONS`) offers only the labels a record decides.
    valueGetter: (p) => (p.data ? ingestionLabel(p.data) : ""),
    cellRenderer: (p: ICellRendererParams<FileRecord>) =>
      p.data ? <IngestionLabelBadge file={p.data} /> : null,
  },
  columnHelper.accessor("version", {
    headerName: "Version",
    minWidth: 90,
    sortable: false,
  }),
  columnHelper.accessor("fs_type", {
    headerName: "FS Type",
    minWidth: 100,
    sortable: false,
  }),
  columnHelper.accessor("parent_id", {
    headerName: "Parent ID",
    minWidth: 180,
    sortable: false,
    cellClass: cellStyles.idCell,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("storage_type", {
    headerName: "Storage Type",
    minWidth: 130,
    sortable: false,
  }),
];

/**
 * `STATIC_FILE_COLUMNS` while `release-ingestion-labels` is off: the Ingestion
 * column shows `ingestion_status` as text, under the same column id.
 */
const STATUS_FILE_COLUMNS: ColDef<FileRecord>[] = STATIC_FILE_COLUMNS.map(
  (column) =>
    colId(column) === INGESTION_COLUMN_ID
      ? columnHelper.accessor("ingestion_status", {
          headerName: "Ingestion Status",
          minWidth: 140,
          sortable: false,
          valueFormatter: (p) =>
            p.value ? INGESTION_STATUS_LABELS[p.value] : "",
          cellRenderer: renderDashOrValue,
        })
      : column,
);

const DEFAULT_VISIBLE_IDS = [
  "name",
  "created",
  "source",
  "association_id",
  "tags",
];

/** The Ingestion column is visible by default only while it shows the labels. */
const LABEL_DEFAULT_VISIBLE_IDS = [...DEFAULT_VISIBLE_IDS, INGESTION_COLUMN_ID];

function pickerOptions(columns: ColDef<FileRecord>[]): ColumnPickerOption[] {
  return columns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));
}

const LABEL_PICKER_OPTIONS = pickerOptions(STATIC_FILE_COLUMNS);
const STATUS_PICKER_OPTIONS = pickerOptions(STATUS_FILE_COLUMNS);

function buildMetadataColumn(metadataKey: string): ColDef<FileRecord> {
  return {
    colId: `metadata.${metadataKey}`,
    headerName: metadataKey,
    // Floor so a flex metadata column doesn't collapse when the grid overflows.
    minWidth: 120,
    valueGetter: (p) => p.data?.metadata?.[metadataKey],
    valueFormatter: (p) => formatMetadataValue(p.value),
    cellRenderer: renderDashOrValue,
    sortable: false,
  };
}

export interface FilesListProps {
  scope: FilesQueryScope;
  /** Bulk-action options surfaced in the DataTable.Selection menu. */
  selectionOptions?: SelectionOption<FileRecord>[];
}

export function FilesList({ scope, selectionOptions = [] }: FilesListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const quickSearchState = useQuickSearchHotkey();
  const { preferences, savePreferences } = useFileTablePreferences();

  const metadataKeysQuery = useFileMetadataKeys(resolvedOrgId);

  // Directories backing the `created_by` / `modified_by` identity filters.
  const identityOptions = useAuditIdentityOptions(resolvedOrgId);

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const ingestionLabelsShown = useCheckFeatureFlag("release-ingestion-labels");

  const columns = React.useMemo<ColDef<FileRecord>[]>(
    () =>
      buildColumnsForPreferences<FileRecord>({
        staticColumns: ingestionLabelsShown
          ? STATIC_FILE_COLUMNS
          : STATUS_FILE_COLUMNS,
        dynamicColumns: (metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        preferenceOrder: initialColumnIds,
      }),
    [ingestionLabelsShown, initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...(ingestionLabelsShown ? LABEL_PICKER_OPTIONS : STATUS_PICKER_OPTIONS),
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [ingestionLabelsShown, metadataKeysQuery.data],
  );

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here — before the table's first query is composed. Seeding
  // it is what keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<FileRecord>({
    getRowId: getFileId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: ingestionLabelsShown
      ? LABEL_DEFAULT_VISIBLE_IDS
      : DEFAULT_VISIBLE_IDS,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Files,
    link: linked,
  });
  const view = useResourceView<FileRecord>(restored.options);

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view, VIEW_LOAD_OPTIONS);
  useRecordTableState({
    target: QueryTarget.Files,
    view,
    loadedView: viewSession.loadedView,
  });
  useResourceTableContext({
    target: QueryTarget.Files,
    view,
    session: viewSession,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  // Filter definitions are restricted to fields the backend exposes via
  // `FILE_FIELDS` in `roboql/model/core.py`. Excluded:
  //   - parent_id, version, association_type, fs_type, storage_type: not in
  //     FILE_FIELDS (backend rejects the condition).
  //   - status: we only surface "available" files, not "reserved" or "deleted",
  //     so this filter would buy us nothing.
  //   - org_id: always implicitly scoped to the current org by the
  //     structured-query backend.
  //   - metadata: dynamic, handled by Milestone 4.
  const searchFileTags = useOrgFileTagSearch(resolvedOrgId);

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () =>
      hydrateFilterDefinitions(
        ingestionLabelsShown
          ? FILE_FILTER_FIELDS
          : FILE_FILTER_FIELDS_BY_STATUS,
        {
          identityOptions,
          setOptions: toOptionsResolver(searchFileTags),
        },
      ),
    [ingestionLabelsShown, identityOptions, searchFileTags],
  );

  const submission = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
    optionConditions: FILE_OPTION_CONDITIONS,
  });

  const query = composeQueryBody({
    scopeCondition: filesScopeToCondition(scope),
    filterCondition: submission.filterCondition,
    sort: submission.sort,
    maxResults: submission.maxResults,
  });

  const listQueryKey = fileQueryKeys.list(resolvedOrgId, scope, query);
  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Files,
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: listQueryKey,
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<FileRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const rows: FileRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const renderExpandedRow = React.useCallback((file: FileRecord) => {
    return (
      <FileMetadataAndTopics
        fileId={file.file_id}
        fileVersion={file.version}
        resourceOwnerId={file.org_id}
        readonly
      />
    );
  }, []);

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = selectionOptions.length > 0;
  const ingestionLabelColumnShown =
    ingestionLabelsShown &&
    view.table.visibleColumnIds.has(INGESTION_COLUMN_ID);
  // A query's results never change once it has run, so the rows are as
  // fresh as the submission they came from.
  const rowsFetchedAt =
    submitQuery.isPlaceholderData || pageQuery.isPlaceholderData
      ? undefined
      : submitQuery.dataUpdatedAt;

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by ID or name…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Files}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={filterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find files"
          />
          <ResourceView.Filters
            ref={filtersRef}
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {hasSelectionOptions && (
            <ResourceView.Selection<FileRecord>
              selection={view.table.selection}
              clearSelection={view.table.clearSelection}
              options={selectionOptions}
            />
          )}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          trailingActions={
            <SaveViewControl
              view={view}
              target={QueryTarget.Files}
              session={viewSession}
            />
          }
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <LatestIngestionRunsProvider
          files={ingestionLabelColumnShown ? rows : NO_FILES}
          recordsFetchedAt={rowsFetchedAt}
          recordsQueryKey={listQueryKey}
          orgId={resolvedOrgId}
        >
          <DataTable<FileRecord>
            defaultColDef={FLEX_FILL_COL_DEF}
            table={view.table}
            hasNextPage={pageQuery.hasNextPage}
            loading={loading}
            columns={columns}
            data={rows}
            noRowsContent={
              // Files reach Roboto by being uploaded to a dataset, never from
              // this list, so the empty org sees where to go rather than a
              // button.
              <ResourceView.EmptyState
                hasActiveFilters={view.hasActiveFilters}
                noData={{
                  icon: FilesIcon,
                  title: "No files yet",
                  description: "Upload files to a dataset to see them here.",
                }}
                noMatches={{
                  icon: FilesIcon,
                  title: "No matching files",
                  description: "Try adjusting or clearing your filters.",
                }}
              />
            }
            totalRowCount={countQuery.data}
            selectionEnabled={hasSelectionOptions}
            expansion={{ renderRow: renderExpandedRow }}
          />
        </LatestIngestionRunsProvider>
      </ResourceView.Root>
    </>
  );
}
